import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import Address from 'models/Address';
import FetchState from 'models/FetchState';
import type { IAddress } from 'models/Address';

type Props = { children: ReactNode };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'reload'; data: IAddress[]; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  addresses: Address[];
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const AddressesContext = createContext<ContextValue | null>(null);

function addressesReducer(value: ContextValue, action: Action): ContextData {
  const { addresses, fetchState } = value;

  switch (action.type) {
    case 'reload': {
      return {
        fetchState,
        addresses: action.data.map((address) => new Address(address)),
      };
    }

    case 'setFetchState': {
      return { addresses, fetchState: action.data };
    }

    default: {
      return value;
    }
  }
}

function AddressesProvider({ children }: Props): ReactElement {
  const [data, dispatch] = useReducer(addressesReducer, {
    fetchState: FetchState.IN_PROGRESS,
    addresses: [],
  });

  const { addresses, fetchState } = data;

  return (
    <AddressesContext.Provider value={{ addresses, fetchState, dispatch }}>
      {children}
    </AddressesContext.Provider>
  );
}

export type { ContextValue };
export { AddressesContext, AddressesProvider };
